import { call, put, select } from 'typed-redux-saga';
import { ActionType } from 'typesafe-actions';
import { APIParams, APIResult, callAPI } from '../../callApi';
import { createAsyncActionResource } from '../util';
import {
  ApiActionType,
  EndPointBuilder,
  HTTPMethod,
  ItemBodyMerger,
  ItemBodyParser,
  ItemSummaryBodyMerger,
  KeyComparer,
  MainResourceHandler,
  MainResourceState,
  MainStateSelector,
  OnSuccessHandler,
  QueryParamSelector,
  SimpleChange,
} from './types';
import { buildRequestBody, createErrorHandler, handleBodyLoaded } from './util';

type SuccessPayload<TItemBody, TItemKey> = {
  frameSeqNumber: number;
  key: TItemKey;
  response: APIResult;
  onSuccess?: OnSuccessHandler<TItemBody>;
};

export function createCancelAction<
  TItemKey,
  TItemSummary extends TItemKey,
  TItemBody extends TItemKey,
  TItemBodyAPI,
>(
  displayName: string,
  getEndpoint: EndPointBuilder<TItemKey, TItemSummary, TItemBody>,
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  sameKey: KeyComparer<TItemKey, TItemSummary, TItemBody>,
  parseItemBody: ItemBodyParser<TItemBody, TItemBodyAPI>,
  mergeItemBody: ItemBodyMerger<TItemBody>,
  mergeSummaryBody: ItemSummaryBodyMerger<TItemSummary, TItemBody>,
  queryParamSelector: QueryParamSelector<TItemBody>,
) {
  const cancelAsync = createAsyncActionResource<
    SimpleChange<TItemBody>,
    SuccessPayload<TItemBody, TItemKey>
  >(displayName, 'cancel');

  function* cancelRequestSaga(
    params: ActionType<typeof cancelAsync.request>,
  ): Generator {
    const state = (yield select(selector)) as MainResourceState<
      TItemKey,
      TItemSummary,
      TItemBody
    >;
    const { currentBody, frameSeqNumber } = state;

    if (!currentBody) {
      console.error('Attempting to cancel without a body');
      return;
    }

    const { onSuccess, onFailure } = params.payload;

    try {
      const endpoint = getEndpoint(currentBody);
      const bodyGET = yield* buildRequestBody(selector);
      const apiParams: APIParams = {
        queryParams: {
          ...(queryParamSelector(currentBody) ?? {}),
          cancel: true,
        },
        body: { bodyGET },
      };
      const response = yield* call(
        callAPI,
        endpoint,
        'POST' as HTTPMethod,
        apiParams,
      );

      yield put(
        cancelAsync.success({
          frameSeqNumber,
          key: currentBody,
          response,
          onSuccess,
        }),
      );
    } catch (error) {
      yield put(
        cancelAsync.failure({
          ...error,
          onFailure,
        }),
      );
    }
  }

  const cancelRequestHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof cancelAsync.request
  > = state => {
    return {
      ...state,
      pendingLoads: state.pendingLoads + 1,
      error: undefined,
      patchData: undefined,
    };
  };

  const cancelSuccessHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof cancelAsync.success
  > = (state, action) => {
    if (action.payload.response.result) {
      return handleBodyLoaded(
        state,
        action.payload.response,
        action.payload.frameSeqNumber,
        false, // cancel is considered a load (a "re-load")
        true,
        false, // new body wins, don't merge with existing body
        sameKey,
        mergeItemBody,
        mergeSummaryBody,
        parseItemBody,
        ApiActionType.cancel,
      );
    } else {
      const key = action.payload.key;
      const records = state.records.filter(
        record => !sameKey(record.summary, key),
      );
      return {
        ...state,
        pendingLoads: state.pendingLoads - 1,
        records,
        currentRecordIndex: undefined,
        currentRecord: undefined,
        currentBody: undefined,
        currentItems: records.map(r => r.summary),
        lastLoadParams: undefined,
        lastRecordsChangedAction: ApiActionType.cancel,
        cancelCount: (state.cancelCount || 0) + 1,
        error: undefined,
      };
    }
  };

  function* cancelSuccessSaga(
    action: ActionType<typeof cancelAsync.success>,
  ): Generator {
    const { onSuccess } = action.payload;

    if (onSuccess) {
      const { currentBody } = (yield select(selector)) as MainResourceState<
        TItemKey,
        TItemSummary,
        TItemBody
      >;
      onSuccess(currentBody);
    }
  }

  function* cancelFailureSaga(
    action: ActionType<typeof cancelAsync.failure>,
  ): Generator {
    const { onFailure } = action.payload;
    onFailure && onFailure();
  }

  const cancelFailureHandler = createErrorHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof cancelAsync.failure
  >(false);

  return {
    cancelAsync,
    cancelRequestSaga,
    cancelRequestHandler,
    cancelSuccessHandler,
    cancelSuccessSaga,
    cancelFailureHandler,
    cancelFailureSaga,
  };
}
